import { ColumnDef, Row, Column, Table as TanStackTable } from "@tanstack/react-table";
import { Trash2 } from "lucide-react";
import { useEffect } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { InvoiceItemType } from "@/types";

// const intl = new Intl.DateTimeFormat('lt-LT', { dateStyle: 'short' })
// const toDate = (date: string) => date ? intl.format(new Date(date)) : '';
const toSumEur = (sum: number) =>
  new Intl.NumberFormat("lt-LT", { style: "currency", currency: "EUR" }).format(sum);

function EditableCell<TData extends InvoiceItemType>({
  row,
  column,
  table,
  type,
  autoFocus,
}: {
  row: Row<TData>;
  column: Column<TData>;
  table: TanStackTable<TData>;
  type?: string;
  autoFocus?: boolean;
}) {
  const { form, disabled } = table.options.meta ?? {};

  if (!form) {
    return null;
  }

  const name = `items[${row.index}].${column.id}`;
  // Using `valueAsNumber` would write `NaN` into the form state while the user
  // is mid-edit (e.g. field momentarily empty between clearing and typing).
  // `NaN` then leaks into the DOM (React warns: `Received NaN for the value
  // attribute`) and silently fails Zod's `z.number().gt(0)` check, which
  // blocks form submission without surfacing a useful error. Coercing empty /
  // `NaN` to `undefined` lets Zod produce a proper "expected number" message
  // and keeps the <input> value a valid string.
  const { onChange, onBlur, ref } = form.register(name as Parameters<typeof form.register>[0], {
    setValueAs:
      type === "number"
        ? (value: unknown) => {
            if (value === "" || value === null || value === undefined) return undefined;
            const parsed = typeof value === "number" ? value : Number(value);
            return Number.isNaN(parsed) ? undefined : parsed;
          }
        : undefined,
  });

  useEffect(() => {
    if (!autoFocus) return;
    document.querySelector<HTMLInputElement>(`input[name="${name}"]`)?.focus();
  }, []);

  return (
    <FormField
      control={form.control as Parameters<typeof FormField>[0]["control"]}
      name={name as never}
      render={({ field }) => {
        return (
          <FormItem>
            <FormControl>
              <Input
                {...field}
                name={name}
                className="text-right"
                type={type ?? "text"}
                ref={ref}
                onChange={onChange}
                onBlur={onBlur}
                onFocus={(e) => e.target.select()}
                disabled={disabled}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}

export const invoiceItemsColumns: ColumnDef<InvoiceItemType>[] = [
  {
    accessorKey: "order",
    header: "Nr.",
    cell: ({ row }) => row.index + 1,
  },
  {
    accessorKey: "product_id",
    header: "ID",
  },
  {
    accessorKey: "name",
    header: "Pavadinimas",
    cell: ({ row }) => {
      const { original } = row;
      return (
        <>
          {original.name}
          {original.latin && <div className="italic text-gray-600">({original.latin})</div>}
        </>
      );
    },
  },
  {
    accessorKey: "quantity",
    header: "Kiekis",
    cell: (cellParams) => <EditableCell {...cellParams} type="number" autoFocus={true} />,
  },
  {
    accessorKey: "unit",
    header: "Vnt.",
    cell: (cellParams) => <EditableCell {...cellParams} />,
  },
  {
    accessorKey: "price",
    header: "Kaina",
    cell: (cellParams) => {
      return (
        <div className="whitespace-nowrap flex items-center">
          <EditableCell {...cellParams} type="number" />
          &nbsp;&euro;
        </div>
      );
    },
  },
  {
    accessorKey: "total",
    header: "Suma",
    cell: ({ row }) => {
      return (
        <div className="text-right">{toSumEur(row.original.quantity * row.original.price)}</div>
      );
    },
  },
  {
    accessorKey: "actions",
    header: "",
    cell: ({ row, table }) => {
      const { disabled, removeRow } = table.options.meta ?? {};
      if (!removeRow) return null;
      return (
        <Button disabled={disabled} onClick={() => removeRow(row.index)} type="button">
          <Trash2 size={16} />
        </Button>
      );
    },
  },
];
